"use client";

import { motion } from "framer-motion";

const defaultPricing = [
  {
    name: "Small",
    desc: "1–2 större föremål som soffa eller kylskåp",
    image: "/service-things-small.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 8 kr/km",
    avgCost: "995 kr",
  },
  {
    name: "Medium",
    desc: "Flera möbler eller skrymmande föremål",
    image: "/service-room-medium.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 12 kr/km",
    avgCost: "1 495 kr",
  },
  {
    name: "Large",
    desc: "Tömning av ett rum eller liten lägenhet",
    image: "/service-space-large.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 15 kr/km",
    avgCost: "2 495 kr",
  },
  {
    name: "X-Large",
    desc: "Komplett tömning av bostad eller kontor",
    image: "/service-whole-place-x-large.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 18 kr/km",
    avgCost: "4 995 kr",
  },
];

import type { Locale } from "./templates/ServicePageTemplate";

const vpT = {
  sv: {
    package: "Paket",
    crew: "Personer",
    framkorning: "Framkörningsavgift",
    kmPrisAB: "KM-pris A-B",
    avgCost: "Snittpris*",
    footnote:
      "* Snittpriset är paketets pris och inkluderar 20 km framkörning. Utgångspunkten är Stockholms centralstation för bokningar i Stockholm och Göteborgs centralstation för bokningar i Göteborg. Överstigande kilometer debiteras enligt paketets km-pris. Varje bokning inkluderar en liten bokningsavgift. Du ser alltid det totala priset innan du bokar.",
  },
  en: {
    package: "Package",
    crew: "Crew",
    framkorning: "Travel fee",
    kmPrisAB: "Distance A→B",
    avgCost: "Avg. cost*",
    footnote:
      "* The average price is the package price and includes 20 km of travel. The starting point is Stockholm Central Station for bookings in Stockholm and Gothenburg Central Station for bookings in Gothenburg. Additional kilometers are charged at the package's per-km rate. Each booking includes a small booking fee. You always see the total price before you book.",
  },
};

interface PricingRowProps {
  name: string;
  desc: string;
  image?: string;
  alt?: string;
  crew: string;
  framkorning: string;
  kmPrisAB?: string;
  avgCost: string;
}

interface ValuePropositionsProps {
  heading?: string;
  priceHint?: string;
  rows?: PricingRowProps[];
  locale?: Locale;
  simplePricing?: boolean;
}

export default function ValuePropositions({
  heading = "Bortforsling prislista",
  priceHint,
  rows = defaultPricing,
  locale = "sv",
  simplePricing = false,
}: ValuePropositionsProps = {}) {
  const l = vpT[locale];
  const hasImages = rows.some((r) => r.image);
  const hasAB = rows.some((r) => r.kmPrisAB);

  // Desktop grid template:
  //   simplePricing → name + avgCost only
  //   else: [image]? + name+desc + crew + framkorning [+ kmPrisAB] + avgCost
  const desktopCols = simplePricing
    ? "md:grid-cols-[1fr_140px]"
    : hasImages
    ? hasAB
      ? "md:grid-cols-[220px_1fr_140px_minmax(180px,1fr)_minmax(180px,1fr)_120px]"
      : "md:grid-cols-[220px_1fr_140px_minmax(200px,1.2fr)_120px]"
    : hasAB
    ? "md:grid-cols-[1fr_140px_minmax(180px,1fr)_minmax(180px,1fr)_120px]"
    : "md:grid-cols-[1fr_140px_minmax(200px,1.2fr)_120px]";

  return (
    <section className="py-8 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark mb-12 sm:mb-16"
        >
          {heading}
        </motion.h2>

        {priceHint && (
          <p className="text-base sm:text-lg text-gray-600 -mt-8 sm:-mt-10 mb-12 sm:mb-16 max-w-3xl leading-relaxed">
            {priceHint}
          </p>
        )}

        {/* Column headers — outside the border, hidden on mobile */}
        <div className={`hidden md:grid ${desktopCols} items-center gap-4 px-10 pb-3`}>
          {hasImages && !simplePricing && <span />}
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider">
            {l.package}
          </span>
          {!simplePricing && (
            <>
              <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider text-center">
                {l.crew}
              </span>
              <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider text-center">
                {l.framkorning}
              </span>
              {hasAB && (
                <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider text-center">
                  {l.kmPrisAB}
                </span>
              )}
            </>
          )}
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider text-right">
            {l.avgCost}
          </span>
        </div>

        <div className="border border-gray-200 rounded-2xl overflow-hidden px-4 sm:px-6">
          {/* Rows */}
          {rows.map((row, i) => (
            <motion.div
              key={row.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.07 }}
              className="border-b border-gray-100 last:border-b-0 rounded-xl transition-colors duration-200 hover:bg-gray-50 cursor-pointer py-2 md:py-4"
              whileHover={{ scale: 1.01 }}
            >
              {/* Desktop row */}
              <div
                className={`hidden md:grid ${desktopCols} ${
                  simplePricing ? "py-5" : "py-5"
                } items-center gap-4 px-4`}
              >
                {/* Truck image */}
                {hasImages && !simplePricing && row.image && (
                  <div className="flex items-center gap-4">
                    {/* eslint-disable-next-line @next/next/no-img-element -- row.image kommer ur raddatan utan mått; next/image kräver width/height eller fill, och filerna är redan webp och sizas av layouten */}
                    <img
                      src={row.image}
                      alt={row.alt ?? row.name}
                      className="h-auto object-contain object-left"
                    />
                  </div>
                )}

                {/* Name + Description */}
                <div>
                  <span className="text-base font-bold text-brand-dark block">
                    {row.name}
                  </span>
                  <span className="text-sm text-gray-500 leading-snug">
                    {row.desc}
                  </span>
                </div>

                {!simplePricing && (
                  <>
                    {/* Crew */}
                    <div className="flex justify-center">
                      <span className="text-sm text-brand-dark text-center whitespace-nowrap">
                        {row.crew}
                      </span>
                    </div>

                    {/* Framkörningsavgift */}
                    <span className="text-sm text-brand-dark text-center leading-snug">
                      {row.framkorning}
                    </span>

                    {/* KM-pris A-B (only when any row has it) */}
                    {hasAB && (
                      <span className="text-sm text-brand-dark text-center leading-snug">
                        {row.kmPrisAB ?? "—"}
                      </span>
                    )}
                  </>
                )}

                {/* Average cost */}
                <span className="text-xl sm:text-2xl font-bold text-brand-dark text-right">
                  {row.avgCost}
                </span>
              </div>

              {/* Mobile card */}
              <div className="md:hidden px-2 py-5 rounded-xl">
                {simplePricing ? (
                  /* Simple mobile layout: name + desc + price */
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-lg font-bold text-brand-dark leading-tight block">
                        {row.name}
                      </span>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">
                        {row.desc}
                      </p>
                    </div>
                    <span className="text-xl font-bold text-brand-dark shrink-0">
                      {row.avgCost}
                    </span>
                  </div>
                ) : (
                  <>
                    {/* Top section: name + crew badge left, image right */}
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-2 pt-1 shrink-0">
                        <span className="text-lg font-bold text-brand-dark leading-tight">
                          {row.name}
                        </span>
                        <span className="inline-flex items-center self-start rounded-md bg-brand-secondary/10 px-2.5 py-1 text-xs font-semibold text-brand-secondary whitespace-nowrap">
                          {row.crew}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col items-center">
                        {row.image && (
                          // eslint-disable-next-line @next/next/no-img-element -- samma raddata som ovan, mobilvyn
                          <img
                            src={row.image}
                            alt={row.alt ?? row.name}
                            className="h-auto object-contain"
                          />
                        )}
                        <p className="text-xs text-gray-500 text-center mt-1.5 leading-snug">
                          {row.desc}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 mt-4 mb-3" />

                    {/* Stat rows: framkorning (+ kmPrisAB) + avgCost */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-3 rounded-lg bg-gray-50 py-2 px-3">
                        <span className="text-[11px] text-gray-500 font-medium shrink-0">
                          {l.framkorning}
                        </span>
                        <span className="text-xs font-semibold text-brand-dark text-right leading-snug">
                          {row.framkorning}
                        </span>
                      </div>
                      {hasAB && (
                        <div className="flex items-start justify-between gap-3 rounded-lg bg-gray-50 py-2 px-3">
                          <span className="text-[11px] text-gray-500 font-medium shrink-0">
                            {l.kmPrisAB}
                          </span>
                          <span className="text-xs font-semibold text-brand-dark text-right leading-snug">
                            {row.kmPrisAB ?? "—"}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3 rounded-lg bg-brand-secondary/10 py-2.5 px-3">
                        <span className="text-[11px] text-brand-secondary font-medium">
                          {l.avgCost.replace("*", "")}
                        </span>
                        <span className="text-base font-bold text-brand-secondary">
                          {row.avgCost}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-6 text-xs text-gray-400 max-w-3xl"
        >
          {l.footnote}
        </motion.p>
      </div>
    </section>
  );
}
